import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Sparkles, Clock, BookOpen, Loader2 } from "lucide-react";
import { Section, Eyebrow, H1, H2, Lead } from "../components/ui-kit";
import { resolveImageUrl } from "../lib/api";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All",
  "Educational Insights",
  "Faculty Corner",
  "Parent Zone",
  "Founder's Journal",
];

const CATEGORY_TONE = {
  "Educational Insights": "#FF8C00",
  "Faculty Corner": "#00FFD0",
  "Parent Zone": "#FFB05E",
  "Founder's Journal": "#FF5A00",
};

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [spotlight, setSpotlight] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API}/blog/spotlight`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/blog/posts`).then((r) => r.data).catch(() => []),
        ]);
        if (!alive) return;
        setSpotlight(s);
        setPosts(p);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const visiblePosts = useMemo(() => {
    const list = posts.filter((p) => !spotlight || p.slug !== spotlight.slug);
    if (active === "All") return list;
    return list.filter((p) => p.category === active);
  }, [posts, active, spotlight]);

  return (
    <div>
      <Seo
        title="Perforange Journal · Insights from India's AVGC Frontline"
        description="Field notes from the Orange Economy — operational reading for principals, faculty, parents, and the people building the national AVGC standard."
        url="https://perforange.com/blog"
      />
      {/* Hero */}
      <Section className="!pt-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-radial-orange pointer-events-none" />
        <div className="relative max-w-4xl">
          <Eyebrow><BookOpen className="w-3 h-3" /> Perforange Journal</Eyebrow>
          <H1>
            Field notes from the <span className="text-gradient-orange">Orange Economy</span>.
          </H1>
          <Lead className="mt-6 max-w-3xl">
            Operational insights from the frontlines of India's AVGC infrastructure — written for principals, faculty, parents, and the people building the standard.
          </Lead>
        </div>
      </Section>

      {/* Spotlight */}
      <Section className="!pt-0">
        {loading ? (
          <div className="rounded-3xl border border-white/8 bg-[#0C0C10] p-16 flex items-center justify-center text-[#A1A1AA]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the journal…
          </div>
        ) : spotlight ? (
          <Link
            to={`/blog/${spotlight.slug}`}
            data-testid="spotlight-card"
            className="block group rounded-3xl overflow-hidden border border-white/10 hover:border-[#FF8C00]/40 hover:shadow-[0_0_80px_rgba(255,140,0,0.15)] transition-all bg-[#0C0C10]"
          >
            <div className="grid lg:grid-cols-12">
              <div className="lg:col-span-7 relative min-h-[280px] lg:min-h-[440px] overflow-hidden">
                {spotlight.cover_image && (
                  <img
                    src={resolveImageUrl(spotlight.cover_image)}
                    alt={spotlight.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-80 transition-opacity"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
                <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-[10px] font-mono-tech font-black uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" /> Spotlight
                </div>
              </div>
              <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-center">
                <p
                  className="text-[10px] font-mono-tech uppercase tracking-[0.25em]"
                  style={{ color: CATEGORY_TONE[spotlight.category] || "#FF8C00" }}
                >
                  {spotlight.category}
                </p>
                <h2 className="mt-3 font-display font-black text-2xl md:text-3xl lg:text-4xl leading-tight text-white">
                  {spotlight.title}
                </h2>
                <p className="mt-4 text-[#A1A1AA] leading-relaxed">{spotlight.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-[#6a6a70] font-mono-tech uppercase tracking-widest">
                  <span>{spotlight.author}</span>
                  <span className="w-1 h-1 rounded-full bg-[#6a6a70]" />
                  <span>{formatDate(spotlight.published_at)}</span>
                  <span className="w-1 h-1 rounded-full bg-[#6a6a70]" />
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {spotlight.read_minutes} min</span>
                </div>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-black text-[#FF8C00]">
                  Read in spotlight <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        ) : (
          <div className="rounded-3xl border border-white/8 bg-[#0C0C10] p-12 text-center text-[#A1A1AA]">
            No spotlight post yet. Check back soon.
          </div>
        )}
      </Section>

      {/* Filter bar */}
      <Section className="!pt-0">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <Eyebrow>The Archive</Eyebrow>
            <H2 className="!text-2xl md:!text-3xl">Browse by track.</H2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
            {CATEGORIES.map((c) => {
              const tone = c === "All" ? "#FF8C00" : CATEGORY_TONE[c];
              const isActive = active === c;
              return (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-all border ${
                    isActive
                      ? "bg-[#FF8C00] text-[#0A0A0C] border-[#FF8C00]"
                      : "bg-white/5 border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/20"
                  }`}
                  style={!isActive ? { color: tone } : undefined}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid */}
        {visiblePosts.length === 0 ? (
          <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-12 text-center text-[#A1A1AA]">
            No posts yet in <span className="text-white font-bold">{active}</span>. Try another track.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {visiblePosts.map((p) => (
              <BlogCard key={p.slug} post={p} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function BlogCard({ post }) {
  const tone = CATEGORY_TONE[post.category] || "#FF8C00";
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group rounded-2xl overflow-hidden border border-white/8 bg-[#0C0C10] hover:border-[#FF8C00]/40 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)] transition-all flex flex-col"
    >
      <div className="relative h-44 overflow-hidden">
        {post.cover_image ? (
          <img
            src={resolveImageUrl(post.cover_image)}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a20] to-[#0A0A0C]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/95 via-transparent to-transparent" />
        <span
          className="absolute top-4 left-4 px-2.5 py-1 rounded-md text-[10px] font-mono-tech font-black uppercase tracking-widest"
          style={{ background: `${tone}1A`, border: `1px solid ${tone}55`, color: tone }}
        >
          {post.category}
        </span>
      </div>
      <div className="flex-1 flex flex-col p-6">
        <h3 className="font-display font-black text-lg md:text-xl text-white leading-snug group-hover:text-[#FF8C00] transition-colors">
          {post.title}
        </h3>
        <p className="mt-3 text-sm text-[#A1A1AA] line-clamp-3 leading-relaxed">{post.excerpt}</p>
        <div className="mt-auto pt-5 flex items-center justify-between text-[11px] text-[#6a6a70] font-mono-tech uppercase tracking-widest">
          <span>{post.author}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read_minutes} min</span>
        </div>
      </div>
    </Link>
  );
}
